// The ACTIVATION step — the one human gate (prototype stepBody 'activate'; brief §3.1/§7 v2). The
// review table renders the REAL machine-authored configuration VERBATIM from the projection — every
// declared pull, the full Skill text, the lifecycle, every palette entry, the chosen view · cadence —
// never a plain-language summary (the anti-omission guarantee). Activate is this view's ONE primary
// action, busy-guarded so a double-click cannot double-route; Save as draft leaves without
// activating. The console sets no approval field anywhere (P-GATE). Tokens only (G-181).

import type { ReactNode } from 'react';
import type { WorkComposerProjection, WorkView } from '../../projections.ts';
import { lifecycleLine } from '../composer-model.ts';
import { GHOST_BUTTON_CLASS, PRIMARY_BUTTON_CLASS } from '../composer-ui.tsx';
import { declaredDatesLine } from '../view-derivation.ts';

function ReviewRow({
  label,
  hook,
  children,
}: {
  readonly label: string;
  readonly hook: string;
  readonly children: ReactNode;
}): React.JSX.Element {
  return (
    <div
      data-review={hook}
      className="grid gap-2 border-t border-border py-3 first:border-t-0 sm:grid-cols-[8rem_1fr] sm:gap-5"
    >
      <div className="ritona-numeric pt-0.5 text-xs uppercase tracking-eyebrow text-text-subtle">
        {label}
      </div>
      <div className="min-w-0 text-sm leading-relaxed text-text">{children}</div>
    </div>
  );
}

/** The View · cadence review line: the chosen axis, cadence, cap — and the date-field
 *  declarations when the drafted definition carries them (read off the projection's axis
 *  availability, the owning derivation). */
function viewCadenceLine(config: WorkComposerProjection, activeView: WorkView): string {
  const base = `${activeView} · ${config.cadenceLabel} · cap ~${String(config.boardCap)}`;
  const dates = declaredDatesLine(config.viewOptions);
  return dates === null ? base : `${base} · ${dates}`;
}

function ActivateReview({
  config,
  activeView,
}: {
  readonly config: WorkComposerProjection;
  readonly activeView: WorkView;
}): React.JSX.Element {
  return (
    <div className="mt-4">
      <ReviewRow label="Declared pulls" hook="bindings">
        {config.bindings.map((binding) => (
          <div key={binding} className="ritona-numeric text-xs text-text-muted">
            {binding}
          </div>
        ))}
      </ReviewRow>
      <ReviewRow label="Skill" hook="skill">
        <p>{config.skill.goal}</p>
        <p className="mt-1 text-xs text-text-muted">{config.skill.constraints}</p>
        <p className="mt-1 text-xs text-text-muted">{config.skill.playbook}</p>
      </ReviewRow>
      <ReviewRow label="Lifecycle" hook="lifecycle">
        <span className="ritona-numeric text-xs text-text-muted">
          {lifecycleLine(config.lifecycle)}
        </span>
      </ReviewRow>
      <ReviewRow label="Palette" hook="palette">
        {config.palette.map((row) => (
          <div key={row.label} className="ritona-numeric text-xs text-text-muted">
            {row.label}
          </div>
        ))}
      </ReviewRow>
      <ReviewRow label="View · cadence" hook="view">
        <span className="ritona-numeric text-xs text-text-muted">
          {viewCadenceLine(config, activeView)}
        </span>
      </ReviewRow>
    </div>
  );
}

export interface ActivateGateProps {
  readonly config: WorkComposerProjection;
  readonly activeView: WorkView;
  readonly busy: boolean;
  readonly onActivate: () => Promise<void>;
  readonly onSaveDraft: () => void;
}

/** The activation gate panel. */
export function ActivateGate({
  config,
  activeView,
  busy,
  onActivate,
  onSaveDraft,
}: ActivateGateProps): React.JSX.Element {
  return (
    <div className="rounded-md border border-bracken">
      <div className="flex items-center justify-between gap-3 rounded-t-md border-b border-bracken bg-bracken-tint px-5 py-4">
        <span className="ritona-numeric text-xs font-medium uppercase tracking-eyebrow text-bracken">
          Activation — the one human gate
        </span>
        <span className="ritona-numeric text-xs text-bracken">one SSO action · one crossing</span>
      </div>
      <div className="px-5 py-5">
        <p className="max-w-prose text-sm leading-relaxed text-text-muted">
          Review the real configuration — the actual pulls and the skill text, not a plain-language
          summary — then activate.
        </p>
        <ActivateReview config={config} activeView={activeView} />
        <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-border pt-5">
          <button
            type="button"
            disabled={busy}
            aria-label={`Activate workflow: ${config.workflowName}`}
            onClick={() => void onActivate()}
            className={PRIMARY_BUTTON_CLASS}
          >
            Activate workflow
          </button>
          <button
            type="button"
            aria-label={`Save as draft: ${config.workflowName}`}
            onClick={onSaveDraft}
            className={GHOST_BUTTON_CLASS}
          >
            Save as draft
          </button>
          <span className="ml-auto max-w-[14rem] text-right text-xs leading-relaxed text-text-subtle">
            Machine-authored config is reviewed here — the anti-omission guarantee.
          </span>
        </div>
      </div>
    </div>
  );
}
